import { ActionTree } from "vuex";
import { extend } from "quasar";
import { StateInterface } from "src/store";
import { CubeStateInterface } from "./state";
import MaObjects from "src/modules/ma/classes/ma_objects";
import MaRefs from "src/modules/ma/classes/ma_refs";

const actions: ActionTree<CubeStateInterface, StateInterface> = {
  async initStore({ state, commit, rootState }, context) {
    if (!state.inited) {
      await this.dispatch("cubes/initDashboards");
      await this.dispatch("cubes/initChartTypes");
      await this.dispatch("cubes/initReports");
      commit("setStateByKey", {
        key: "inited",
        value: true,
      });
    }
  },

  async initDashboards({ state, commit, rootState }, context) {
    const dashboardObjs = await MaObjects.search({
      type: MaRefs.ref.types.client_dashboard,
      skip: 0,
      limit: 100,
      backlist: undefined,
      text: undefined,
      visible: undefined,
    });
    // console.log("dashboardObjs", dashboardObjs);
    const dashboards = dashboardObjs.map((x: any) => ({
      key: MaObjects.obj2str(x),
      name: x.name,
      reports: x.items.reports?.map((y: any) => MaObjects.ref2str(y)) ?? [],
      reports_layout:
        x.items.reports_layout?.map((y: any) => ({
          ...y,
          report: MaObjects.ref2str(y.report),
        })) ?? [],
      app: MaObjects.ref2str(x.items.app),
    }));
    // console.log("dashboards", dashboards);
    commit("setStateByKey", {
      key: "dashboards",
      value: dashboards,
    });
  },

  async initChartTypes({ state, commit, rootState }, context) {
    const chartTypeObjs = await MaObjects.search({
      type: MaRefs.ref.types.client_dashboard_chart_type,
      skip: 0,
      limit: 100,
      backlist: undefined,
      text: undefined,
      visible: undefined,
    });
    // console.log("chartTypeObjs", chartTypeObjs);
    const chartTypes = chartTypeObjs.map((x: any) => ({
      ...x.items,
      ref: MaObjects.obj2str(x),
      options: x.items.options.reduce((result: any, current: any) => {
        result[current.key] = current.value;
        return result;
      }, {}),
    }));
    // console.log("chartTypes", chartTypes);
    commit("setStateByKey", {
      key: "chartTypes",
      value: chartTypes,
    });
  },

  async initReports({ state, commit, rootState }, context) {
    const reportObjs = await MaObjects.search({
      type: MaRefs.ref.types.client_dashboard_report,
      skip: 0,
      limit: 100000,
      backlist: undefined,
      text: undefined,
      visible: undefined,
    });
    // console.log("reportObjs", reportObjs);

    if (!state.chartTypes.length) {
      await this.dispatch("cubes/initChartTypes");
    }

    const reports = reportObjs.map((x: any) => ({
      key: MaObjects.obj2str(x),
      title: x.name,
      chartType: state.chartTypes.find(
        (y) => y.ref == MaObjects.ref2str(x.items.chart_type)
      ),
      cubes: x.items.cubes ? JSON.parse(x.items.cubes) : [],
      cubejsQuery: x.items.cubejs_query ? JSON.parse(x.items.cubejs_query) : {},
      subscribe: x.items.subscribe,
      export_only: x.items.export_only,
    }));
    // console.log("reports", reports);
    commit("setStateByKey", {
      key: "reports",
      value: reports,
    });
  },

  async getReport({ state, commit, rootState }, key) {
    let report = state.reports.find((x) => x.key == key);
    if (!report) {
      if (!state.chartTypes.length) {
        await this.dispatch("cubes/initChartTypes");
      }
      const reportObj = await MaObjects.getObject(key);
      commit("putReportFromMaObj", reportObj);
      report = state.reports.find((x) => x.key == key);
    }

    return extend(true, {}, report);
  },

  deleteReport({ state, commit, rootState }, key) {
    MaObjects.remove(MaObjects.str2ref(key), true);
    commit("deleteReport", key);

    let i = -1;
    do {
      i = state.dashboards.findIndex((x) =>
        x.reports_layout.some((y: any) => y.report == key)
      );
      if (i != -1) {
        commit("deleteReportFromDashboard", {
          reportKey: key,
          dashboardKey: state.dashboards[i].key,
        });
        this.dispatch("cubes/saveDashboardLayout", {
          dashboard_key: state.dashboards[i].key,
          reports_layout: state.dashboards[i].reports_layout,
        });
      }
    } while (i != -1);
  },

  async putReport({ state, commit, rootState }, report) {
    // console.log("putReport", report);
    const reportObj = await MaObjects.save(
      {
        name: report.title,
        chart_type: MaObjects.str2ref(report.chartType.ref),
        cubes: JSON.stringify(report.cubes || "[]"),
        cubejs_query: JSON.stringify(report.cubejsQuery || "{}"),
        subscribe: report.subscribe,
        export_only: report.export_only,
      },
      report.key ? MaObjects.str2ref(report.key) : undefined,
      MaRefs.ref.types.client_dashboard_report
    );
    if (reportObj) {
      // console.log("reportObj", reportObj);
      commit("putReportFromMaObj", reportObj);
    }
    return MaObjects.obj2str(reportObj);
  },

  async saveDashboardsForReport(
    { state, commit, rootState },
    { reportKey, dashboardKeys }
  ) {
    // console.log("saveReportToDashboards", reportKey, dashboardKeys);
    if (!state.dashboards.length) {
      await this.dispatch("cubes/initDashboards");
    }
    for (let i = 0; i < state.dashboards.length; i++) {
      const dashboard = state.dashboards[i];
      const existing =
        dashboard.reports_layout.findIndex((x: any) => x.report == reportKey) !=
        -1;
      const mustHave =
        dashboardKeys.findIndex((x: any) => x == dashboard.key) != -1;
      if ((mustHave && !existing) || (!mustHave && existing)) {
        this.dispatch("cubes/changeReportToDashboard", {
          reportKey,
          dashboardKey: dashboard.key,
          action: mustHave && !existing ? "add" : "delete",
        });
      }
    }
  },

  async changeReportToDashboard(
    { state, commit, rootState },
    { reportKey, dashboardKey, action }
  ) {
    // console.log("changeReportToDashboard", reportKey, dashboardKey, action);
    if (!state.dashboards.length) {
      await this.dispatch("cubes/initDashboards");
    }
    const mutation =
      action == "add" ? "addReportToDashboard" : "deleteReportFromDashboard";
    commit(mutation, { reportKey, dashboardKey });
    const dashboard = state.dashboards.find((x) => x.key == dashboardKey);
    // console.log("changeReportToDashboard dashboard", dashboard);
    // console.log("changeReportToDashboard state.dashboards", state.dashboards);
    await MaObjects.save(
      {
        reports_layout: dashboard.reports_layout.map((x: any) => ({
          ...x,
          report: MaObjects.str2ref(x.report),
        })),
      },
      MaObjects.str2ref(dashboard.key),
      MaRefs.ref.types.client_dashboard
    );
  },

  async saveDashboardLayout(
    { state, commit, rootState },
    { dashboard_key, reports_layout }
  ) {
    // console.log("saveDashboardLayout", dashboard_key, reports_layout);
    commit("saveDashboardLayout", { dashboard_key, reports_layout });
    await MaObjects.save(
      {
        reports_layout: reports_layout.map((x: any) => ({
          ...x,
          report: MaObjects.str2ref(x.report),
        })),
      },
      MaObjects.str2ref(dashboard_key),
      MaRefs.ref.types.client_dashboard
    );
  },
};

export default actions;
